import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"
import _imports_0 from '../../../public/img/learning/what-is-a-dns-zone/example-zone.jpg'


const _hoisted_1 = /*#__PURE__*/_createElementVNode("strong", { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }, "A DNS zone represents a portion of the DNS namespace owned and managed as a single unit by one organization. The global DNS is composed of many zones.", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The most well-known zones are the DNS root zone, where the DNS begins, and the Top Level Domains (TLDs) such as .com, .net, .org, etc. Below the TLDs, virtually every recognizable brand with a presence on the Internet has its very own DNS zone. So, for example, individual DNS zones exist for "),
  /*#__PURE__*/_createElementVNode("code", null, "google.com"),
  /*#__PURE__*/_createTextVNode(", "),
  /*#__PURE__*/_createElementVNode("code", null, "wikipedia.org"),
  /*#__PURE__*/_createTextVNode(", "),
  /*#__PURE__*/_createElementVNode("code", null, "bit.ly"),
  /*#__PURE__*/_createTextVNode(", and millions upon millions of other domains.")
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/what-is-a-dns-zone/what-is-a-dns-zone.jpg",
    alt: "Zones in the DNS are organized in a tree",
    title: "Zones in the DNS are organized in a tree",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("Zones in the DNS are organized in a tree. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("h2", null, "Where does a zone begin?", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Every DNS zone begins at a specific name in the DNS. The DNS zone owns that name and all of the children of that name. For example, the "),
  /*#__PURE__*/_createElementVNode("code", null, ".com"),
  /*#__PURE__*/_createTextVNode(" TLD is a zone that begins at the name \"com\" and extends downwards to encompass all of the DNS names that end with \".com\".")
], -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("h2", null, "And where does a zone end?", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Each zone owns the name where the zone starts and of that name's children. But each zone's administrator can insert "),
  /*#__PURE__*/_createElementVNode("strong", null, "delegations"),
  /*#__PURE__*/_createTextVNode(" to transfer, or \"delegate\", ownership to other zones.")
], -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("h2", null, "An example zone", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("pre", null, "example.org. 3600 SOA ns1.example.org. admin.example.org. 100 7200 3600 86400 300\nexample.org. 3600 NS ns1.example.org.\nexample.org. 3600 NS ns2.example.org.\n\ndatabase.example.org. 3600 A 10.0.0.3\ndatabase.example.org. 3600 A 10.0.0.4\n\nmail.example.org. 3600 A 10.0.0.1\nmail.example.org. 3600 A 10.0.0.2\n\n; The 'support' subdomain is delegated to DNS servers of a third party\nsupport.example.org. 3600 NS ns1.support-software.org.\nsupport.example.org. 3600 NS ns2.support-software.org.\n", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" zone owns, or is authoritative for, the DNS name"),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(". It also owns all of the DNS names that are children of "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(", with the exception of "),
  /*#__PURE__*/_createElementVNode("code", null, "support.example.org"),
  /*#__PURE__*/_createTextVNode(". The delegation at "),
  /*#__PURE__*/_createElementVNode("code", null, "support.example.org"),
  /*#__PURE__*/_createTextVNode(" means that authority at that point in the DNS tree is delegated to other DNS servers.")
], -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("h3", null, "What names in the DNS belong to the example.org zone?", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", null, "It's helpful to consider the DNS as a tree of names starting with the DNS root at the top and flowing downward. Each node in this tree represents one label of a DNS name.", -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("As we discussed, the "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" zone begins at example.org and flows downwards to all child names, stopping at delegations. So names shown as spheres on green in the diagram below are part of the "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(". Names shown as cubes on purple belong a different zone in the DNS.")
], -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("img", {
  class: "full-width",
  src: _imports_0,
  alt: "Our example zone",
  title: "Our example zone"
}, null, -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("h2", null, "How do DNS lookups use DNS zones?", -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("strong", null, "DNS resolver", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("A DNS resolver performing a DNS lookup for a typical name such as "),
  /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
  /*#__PURE__*/_createTextVNode(" will move through three DNS zones:")
], -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "The root"),
  /*#__PURE__*/_createTextVNode(": Authority begins with the DNS root zone. The root zone contains a delegation to the "),
  /*#__PURE__*/_createElementVNode("code", null, "org"),
  /*#__PURE__*/_createTextVNode(" zone.")
], -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "org"),
  /*#__PURE__*/_createTextVNode(": The "),
  /*#__PURE__*/_createElementVNode("code", null, "org"),
  /*#__PURE__*/_createTextVNode(" zone contains a delegation to the "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" zone.")
], -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("strong", null, "example.org", -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("code", null, "example.org", -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("code", null, "www.example.org", -1)
const _hoisted_23 = { class: "mx-auto max-w-3xl divide-y-2 divide-gray-200" }
const _hoisted_24 = /*#__PURE__*/_createElementVNode("h2", { class: "ignore-toc" }, "Related questions", -1)
const _hoisted_25 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_26 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " What is a DNS zone file? ", -1)
const _hoisted_27 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_28 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "The zone file is a standardized text file format for the DNS. DNS server software such as BIND, Unbound, Knot, and many others read the DNS records for each DNS zone that they host from a DNS zone file.")
], -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("p", null, "Each DNS record in a zone is represented by one line in the zone file. Each line includes the owner name of the DNS record, the Time-To-Live (TTL) of the record in seconds, the record type, and the record data. Each DNS zone file contains all of the records for a single DNS zone.", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("p", null, "Today, many DNS providers have a web-based or scriptable portal that can be used instead of DNS zone files. But these providers usually offer a way to import a DNS zone file. The DNS zone file format is also convenient for examples in articles on the DNS!", -1)
const _hoisted_31 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_32 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " What is DNS zone transfer? ", -1)
const _hoisted_33 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_34 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "Zone transfer is used to copy or \"transfer\" the contents of a DNS zone from one DNS server to another over the Internet using the DNS protocol.")
], -1)
const _hoisted_35 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("DNS zone transfer was introduced in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1034",
    rel: "noopener noreferrer"
  }, "RFC 1034"),
  /*#__PURE__*/_createTextVNode(" and "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1035",
    rel: "noopener noreferrer"
  }, "RFC 1035"),
  /*#__PURE__*/_createTextVNode(" and later updated by "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc5936",
    rel: "noopener noreferrer"
  }, "RFC 5936"),
  /*#__PURE__*/_createTextVNode(". Each zone transfer includes every record in a DNS zone. Incremental zone transfer was added in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1995",
    rel: "noopener noreferrer"
  }, "RFC 1995"),
  /*#__PURE__*/_createTextVNode(" to improve efficiency.")
], -1)
const _hoisted_36 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Zone transfer allows a DNS administrator to host a zone on multiple DNS servers for redundancy without having to manually copy the zone to each DNS server whenever a change is made. The administrator makes changes on one DNS server, called the "),
  /*#__PURE__*/_createElementVNode("strong", null, "primary server"),
  /*#__PURE__*/_createTextVNode(", and zone transfer automatically updates all of the other DNS servers for the zone, called the "),
  /*#__PURE__*/_createElementVNode("strong", null, "secondary servers"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_37 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_38 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " How many zones can be involved in a single DNS lookup? ", -1)
const _hoisted_39 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_40 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "Up to 63! But usually three to five.")
], -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("In our example lookup for "),
  /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
  /*#__PURE__*/_createTextVNode(" there were three zones but the lookup path from the root can flow through more than three zones. In theory there can be up to 63 labels in a DNS name which could mean up to 63 distinct zones! But between three and five labels, and roughly that number of zones, is typical.")
], -1)
const _hoisted_42 = /*#__PURE__*/_createElementVNode("p", null, "Each label in a DNS name may lie in a different DNS zone, or a single zone may encompass multiple labels.", -1)

import BlogPost from '@/components/layout/BlogPost.vue';

import { useRouter } from 'vue-router';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { jeff, ruurtjan } from '@/common/authors';
import { learningBreadcrumb } from '@/model/Breadcrumb';
import { BlogMeta } from '@/common/blogMeta';
import CodeBlock from '@/components/micro/CodeBlock.vue';

const meta: BlogMeta = {
  tag: 'DNS concepts',
  url: '/learning/what-is-a-dns-zone/',
  breadcrumbs: [learningBreadcrumb],
  title: 'What is a DNS zone?',
  description: 'A DNS zone represents a portion of the DNS namespace owned and managed as a single unit by one organization. The global DNS is composed of many zones.',
  overviewImage: '/img/learning/what-is-a-dns-zone/what-is-a-dns-zone-small.png',
  overviewImageAlt: 'What is a DNS zone?',
  overviewDescription: 'A DNS zone represents a portion of the DNS namespace owned and managed as a single unit by one organization. The global DNS is composed of many zones.',
  author: jeff,
  editor: ruurtjan,
  img1024x512: 'https://www.nslookup.io/img/learning/dns-what-is-a-dns-zone/dns-what-is-a-dns-zone-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/dns-what-is-a-dns-zone/dns-what-is-a-dns-zone-social.jpg',
  written: new Date('2022-12-01T09:02:05.061Z'),
  updated: new Date('2023-03-28T12:01:00.156Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'WhatIsADnsZone',
  setup(__props) {


return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/what-is-a-dns-zone/what-is-a-dns-zone.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _hoisted_1,
        _hoisted_2,
        _hoisted_3,
        _hoisted_4,
        _hoisted_5,
        _createElementVNode("p", null, [
          _createTextVNode("The DNS contains a special record at the start of each zone: the "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/soa/" }, {
            default: _withCtx(() => [
              _createTextVNode("Start of Authority (SOA) record")
            ]),
            _: 1
          }),
          _createTextVNode(". This record signifies the beginning of a zone and contains some important metadata about the zone. All zones start with an SOA record.")
        ]),
        _hoisted_6,
        _hoisted_7,
        _createElementVNode("p", null, [
          _createTextVNode("A "),
          _createVNode(_component_router_link, { to: "/learning/zone-delegation/" }, {
            default: _withCtx(() => [
              _createTextVNode("delegation")
            ]),
            _: 1
          }),
          _createTextVNode(" serves two important purposes. First, a delegation marks the end of the zone's authority. The delegated name and all of its children are not part of the zone: they are instead part of the child zone. Second, the delegation publishes, to the entire Internet, the set of DNS servers that can resolve names in the child zone.")
        ]),
        _createElementVNode("p", null, [
          _createTextVNode("In this way, the global DNS begins at "),
          _createVNode(_component_router_link, {
            to: "/learning/what-is-the-dns-root-zone/",
            title: "What is the DNS root zone?"
          }, {
            default: _withCtx(() => [
              _createTextVNode("the root zone")
            ]),
            _: 1
          }),
          _createTextVNode(" and flows downwards to different zones through delegations.")
        ]),
        _hoisted_8,
        _createElementVNode("p", null, [
          _createTextVNode("Let's consider a simple example zone file. The zone starts with an "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/soa/" }, {
            default: _withCtx(() => [
              _createTextVNode("SOA")
            ]),
            _: 1
          }),
          _createTextVNode(" record. There must also be two or more "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/ns/" }, {
            default: _withCtx(() => [
              _createTextVNode("NS records")
            ]),
            _: 1
          }),
          _createTextVNode(" at the root of the zone. And often "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/mx/" }, {
            default: _withCtx(() => [
              _createTextVNode("MX records")
            ]),
            _: 1
          }),
          _createTextVNode(" for mail delivery, and other records as well.")
        ]),
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_9
          ]),
          _: 1
        }),
        _hoisted_10,
        _hoisted_11,
        _hoisted_12,
        _hoisted_13,
        _hoisted_14,
        _hoisted_15,
        _createElementVNode("p", null, [
          _createTextVNode("A DNS lookup occurs when a piece of software, such as a web browser, needs to map a DNS name to an IP address or another piece of DNS data. The result of a DNS lookup is often an IP address, but it may be something else. For example, a DNS "),
          _createVNode(_component_router_link, { to: "/mx-lookup/" }, {
            default: _withCtx(() => [
              _createTextVNode("MX record lookup")
            ]),
            _: 1
          }),
          _createTextVNode(" is used to retrieve the mail servers for a domain.")
        ]),
        _createElementVNode("p", null, [
          _createTextVNode("DNS lookups are performed by a software component called a "),
          _hoisted_16,
          _createTextVNode(". The DNS resolver begins each query at the DNS root domain by sending a DNS query to one of the DNS root servers. The root server will usually respond with a delegation. The resolver then sends the same query to one of the DNS servers listed in the delegation. These servers might respond with another delegation. This process continues until the DNS resolver gets an answer from one of the DNS servers "),
          _createVNode(_component_router_link, { to: "/learning/recursive-vs-authoritative-dns/" }, {
            default: _withCtx(() => [
              _createTextVNode("authoritative")
            ]),
            _: 1
          }),
          _createTextVNode(" for the zone.")
        ]),
        _hoisted_17,
        _createElementVNode("ul", null, [
          _hoisted_18,
          _hoisted_19,
          _createElementVNode("li", null, [
            _hoisted_20,
            _createTextVNode(": The "),
            _hoisted_21,
            _createTextVNode(" zone would typically contain "),
            _createVNode(_component_router_link, { to: "/learning/dns-record-types/a/" }, {
              default: _withCtx(() => [
                _createTextVNode("A")
              ]),
              _: 1
            }),
            _createTextVNode(" or "),
            _createVNode(_component_router_link, { to: "/learning/dns-record-types/aaaa/" }, {
              default: _withCtx(() => [
                _createTextVNode("AAAA")
              ]),
              _: 1
            }),
            _createTextVNode(" records for "),
            _hoisted_22,
            _createTextVNode(".")
          ])
        ]),
        _createElementVNode("div", _hoisted_23, [
          _hoisted_24,
          _createElementVNode("div", _hoisted_25, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_26,
                    _createElementVNode("span", _hoisted_27, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_28,
                    _hoisted_29,
                    _hoisted_30
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_31, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_32,
                    _createElementVNode("span", _hoisted_33, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_34,
                    _hoisted_35,
                    _hoisted_36
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_37, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_38,
                    _createElementVNode("span", _hoisted_39, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_40,
                    _hoisted_41,
                    _hoisted_42
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})